import { createCmlContentDefinitionValueString } from 'bundles/cml';
import { REFUND_POLICY_LINK } from 'bundles/payments-common/common/constants';
import type { FaqFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/SharedCommonEntitiesFragments';

import _t from 'i18n!nls/xdp';

export const getGuidedProjectFaqs = (canEnrollWithFullDiscount = false): Array<FaqFragment> => {
  const policyLink = `<a href="${REFUND_POLICY_LINK}" target = "_blank">${_t('See our full refund policy')}</a>`;
  const refundPolicyAnswer = _t('Guided Projects are not eligible for refunds. #{policyLink}.', {
    policyLink,
  });
  const refundPolicyAnswerCMLText = createCmlContentDefinitionValueString(refundPolicyAnswer);

  const purchaseGuidedProject = {
    id: '1',
    question: {
      dtdId: '1',
      cmlValue: createCmlContentDefinitionValueString(_t('What will I get if I purchase a Guided Project?')),
      htmlWithMetadata: {
        html: '',
        metadata: {
          hasAssetBlock: false,
          hasCodeBlock: false,
          hasMath: false,
          isPlainText: true,
        },
      },
    },
    answer: {
      dtdId: '1',
      cmlValue: createCmlContentDefinitionValueString(
        _t(
          "By purchasing a Guided Project, you'll get everything you need to complete the Guided Project including access to a cloud desktop workspace through your web browser that contains the files and software you need to get started, plus step-by-step video instruction from a subject matter expert."
        )
      ),
      htmlWithMetadata: {
        html: '',
        metadata: {
          hasAssetBlock: false,
          hasCodeBlock: false,
          hasMath: false,
          isPlainText: true,
        },
      },
    },
  };

  const availableDesktopMobile = {
    id: '2',
    question: {
      dtdId: '1',
      cmlValue: createCmlContentDefinitionValueString(_t('Are Guided Projects available on desktop and mobile?')),
      htmlWithMetadata: {
        html: '',
        metadata: {
          hasAssetBlock: false,
          hasCodeBlock: false,
          hasMath: false,
          isPlainText: true,
        },
      },
    },
    answer: {
      dtdId: '1',
      cmlValue: createCmlContentDefinitionValueString(
        _t(
          'Because your workspace contains a cloud desktop that is sized for a laptop or desktop computer, Guided Projects are not available on your mobile device.'
        )
      ),
      htmlWithMetadata: {
        html: '',
        metadata: {
          hasAssetBlock: false,
          hasCodeBlock: false,
          hasMath: false,
          isPlainText: true,
        },
      },
    },
  };

  const instructorsForGuidedProjects = {
    id: '3',
    question: {
      dtdId: '1',
      cmlValue: createCmlContentDefinitionValueString(_t('Who are the instructors for Guided Projects? ')),
      htmlWithMetadata: {
        html: '',
        metadata: {
          hasAssetBlock: false,
          hasCodeBlock: false,
          hasMath: false,
          isPlainText: true,
        },
      },
    },
    answer: {
      dtdId: '1',
      cmlValue: createCmlContentDefinitionValueString(
        _t(
          'Guided Project instructors are subject matter experts who have experience in the skill, tool or domain of their project and are passionate about sharing their knowledge to impact millions of learners around the world.'
        )
      ),
      htmlWithMetadata: {
        html: '',
        metadata: {
          hasAssetBlock: false,
          hasCodeBlock: false,
          hasMath: false,
          isPlainText: true,
        },
      },
    },
  };

  const completeGuidedProject = {
    id: '4',
    question: {
      dtdId: '1',
      cmlValue: createCmlContentDefinitionValueString(
        _t('Can I download the work from my Guided Project after I complete it?')
      ),
      htmlWithMetadata: {
        html: '',
        metadata: {
          hasAssetBlock: false,
          hasCodeBlock: false,
          hasMath: false,
          isPlainText: true,
        },
      },
    },
    answer: {
      dtdId: '1',
      cmlValue: createCmlContentDefinitionValueString(
        _t(
          'You can download and keep any of your created files from the Guided Project. To do so, you can use the “File Browser” feature while you are accessing your cloud desktop.'
        )
      ),
      htmlWithMetadata: {
        html: '',
        metadata: {
          hasAssetBlock: false,
          hasCodeBlock: false,
          hasMath: false,
          isPlainText: true,
        },
      },
    },
  };

  const refundPolicy = {
    id: '4',
    question: {
      dtdId: '1',
      cmlValue: createCmlContentDefinitionValueString(_t('What is the refund policy?')),
      htmlWithMetadata: {
        html: '',
        metadata: {
          hasAssetBlock: false,
          hasCodeBlock: false,
          hasMath: false,
          isPlainText: true,
        },
      },
    },
    answer: {
      dtdId: '1',
      cmlValue: refundPolicyAnswerCMLText,
      htmlWithMetadata: {
        html: '',
        metadata: {
          hasAssetBlock: false,
          hasCodeBlock: false,
          hasMath: false,
          isPlainText: true,
        },
      },
    },
  };

  const financialAidAvailable = {
    id: '5',
    question: {
      dtdId: '1',
      cmlValue: createCmlContentDefinitionValueString(_t('Is financial aid available?')),
      htmlWithMetadata: {
        html: '',
        metadata: {
          hasAssetBlock: false,
          hasCodeBlock: false,
          hasMath: false,
          isPlainText: true,
        },
      },
    },
    answer: {
      dtdId: '1',
      cmlValue: createCmlContentDefinitionValueString(_t('Financial aid is not available for Guided Projects.')),
      htmlWithMetadata: {
        html: '',
        metadata: {
          hasAssetBlock: false,
          hasCodeBlock: false,
          hasMath: false,
          isPlainText: true,
        },
      },
    },
  };

  const auditWatchVideoForFree = {
    id: '6',
    question: {
      dtdId: '1',
      cmlValue: createCmlContentDefinitionValueString(
        _t('Can I audit a Guided Project and watch the video portion for free?')
      ),
      htmlWithMetadata: {
        html: '',
        metadata: {
          hasAssetBlock: false,
          hasCodeBlock: false,
          hasMath: false,
          isPlainText: true,
        },
      },
    },
    answer: {
      dtdId: '1',
      cmlValue: createCmlContentDefinitionValueString(_t('Auditing is not available for Guided Projects.')),
      htmlWithMetadata: {
        html: '',
        metadata: {
          hasAssetBlock: false,
          hasCodeBlock: false,
          hasMath: false,
          isPlainText: true,
        },
      },
    },
  };

  const howMuchExperience = {
    id: '7',
    question: {
      dtdId: '1',
      cmlValue: createCmlContentDefinitionValueString(_t('How much experience do I need to do this Guided Project?')),
      htmlWithMetadata: {
        html: '',
        metadata: {
          hasAssetBlock: false,
          hasCodeBlock: false,
          hasMath: false,
          isPlainText: true,
        },
      },
    },
    answer: {
      dtdId: '1',
      cmlValue: createCmlContentDefinitionValueString(
        _t(
          'At the top of the page, you can press on the experience level for this Guided Project to view any knowledge prerequisites. For every level of Guided Project, your instructor will walk you through step-by-step.'
        )
      ),
      htmlWithMetadata: {
        html: '',
        metadata: {
          hasAssetBlock: false,
          hasCodeBlock: false,
          hasMath: false,
          isPlainText: true,
        },
      },
    },
  };

  const installSpecialSoftware = {
    id: '8',
    question: {
      dtdId: '1',
      cmlValue: createCmlContentDefinitionValueString(
        _t('Can I complete this Guided Project right through my web browser, instead of installing special software?')
      ),
      htmlWithMetadata: {
        html: '',
        metadata: {
          hasAssetBlock: false,
          hasCodeBlock: false,
          hasMath: false,
          isPlainText: true,
        },
      },
    },
    answer: {
      dtdId: '1',
      cmlValue: createCmlContentDefinitionValueString(
        _t(
          'Yes, everything you need to complete your Guided Project will be available in a cloud desktop that is available in your browser.'
        )
      ),
      htmlWithMetadata: {
        html: '',
        metadata: {
          hasAssetBlock: false,
          hasCodeBlock: false,
          hasMath: false,
          isPlainText: true,
        },
      },
    },
  };

  const learningExperience = {
    id: '9',
    question: {
      dtdId: '1',
      cmlValue: createCmlContentDefinitionValueString(_t('What is the learning experience like with Guided Projects?')),
      htmlWithMetadata: {
        html: '',
        metadata: {
          hasAssetBlock: false,
          hasCodeBlock: false,
          hasMath: false,
          isPlainText: true,
        },
      },
    },
    answer: {
      dtdId: '1',
      cmlValue: createCmlContentDefinitionValueString(
        _t(
          "You'll learn by doing through completing tasks in a split-screen environment directly in your browser. On the left side of the screen, you'll complete the task in your workspace. On the right side of the screen, you'll watch an instructor walk you through the project, step-by-step."
        )
      ),
      htmlWithMetadata: {
        html: '',
        metadata: {
          hasAssetBlock: false,
          hasCodeBlock: false,
          hasMath: false,
          isPlainText: true,
        },
      },
    },
  };

  if (canEnrollWithFullDiscount) {
    return [
      availableDesktopMobile,
      instructorsForGuidedProjects,
      completeGuidedProject,
      howMuchExperience,
      installSpecialSoftware,
      learningExperience,
    ];
  } else {
    return [
      purchaseGuidedProject,
      availableDesktopMobile,
      instructorsForGuidedProjects,
      completeGuidedProject,
      refundPolicy,
      financialAidAvailable,
      auditWatchVideoForFree,
      howMuchExperience,
      installSpecialSoftware,
      learningExperience,
    ];
  }
};
